import React, { useState } from 'react';
import { Edit2, Trash2, User } from 'lucide-react';
import { StickyNote, STICKY_COLORS } from '../types';

interface StickyNoteComponentProps {
  note: StickyNote;
  onUpdate: (note: StickyNote) => void;
  onDelete: (noteId: string) => void;
}

const StickyNoteComponent: React.FC<StickyNoteComponentProps> = ({
  note,
  onUpdate,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(note.content);
  const [selectedColor, setSelectedColor] = useState(note.color);

  const handleSave = () => {
    if (content.trim()) {
      onUpdate({
        ...note,
        content: content.trim(),
        color: selectedColor,
      });
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setContent(note.content);
    setSelectedColor(note.color);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      onDelete(note.id);
    }
  };

  const stickyColorClass = STICKY_COLORS.find(c => c.name === note.color)?.class || 'bg-sticky-yellow';

  if (isEditing) {
    return (
      <div className={`sticky-note ${stickyColorClass} min-h-[120px] animate-sticky-in`}>
        <div className="space-y-3">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full bg-transparent border-none resize-none focus:outline-none text-gray-800 placeholder-gray-500"
            rows={3}
            placeholder="Write your note here..."
            autoFocus
          />
          
          {/* Color picker */}
          <div className="flex flex-wrap gap-1">
            {STICKY_COLORS.map((color) => (
              <button
                key={color.name}
                onClick={() => setSelectedColor(color.name)}
                className={`w-6 h-6 rounded-full border-2 transition-all ${color.class} ${
                  selectedColor === color.name 
                    ? 'border-gray-800 scale-110' 
                    : 'border-gray-400 hover:scale-105'
                }`}
              />
            ))}
          </div>
          
          {/* Action buttons */}
          <div className="flex justify-end space-x-2 pt-2">
            <button
              onClick={handleCancel}
              className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!content.trim()}
              className="px-3 py-1 text-xs bg-gray-800 text-white rounded hover:bg-gray-900 disabled:bg-gray-400 transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`sticky-note ${stickyColorClass} group cursor-pointer animate-sticky-in`}
      onClick={() => setIsEditing(true)}
    >
      <div className="space-y-3">
        <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap">
          {note.content}
        </p>
        
        <div className="flex items-center justify-between text-xs text-gray-600">
          <div className="flex items-center space-x-1">
            <User className="h-3 w-3" />
            <span>{note.author || 'Anonymous'}</span>
          </div>
          
          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              className="p-1 hover:bg-black/10 rounded transition-colors"
              title="Edit note"
            >
              <Edit2 className="h-3 w-3" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              className="p-1 hover:bg-black/10 rounded transition-colors text-red-600"
              title="Delete note"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        </div>
        
        <div className="text-xs text-gray-500">
          {new Date(note.timestamp).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </div>
      </div>
    </div>
  );
};

export default StickyNoteComponent;